import { ControllerParams, CreateControllerFn } from '@wix/yoshi-flow-editor';
import { BookingsDataCapsule } from '@wix/bookings-data-capsule';
import { FormStatus } from '../../types/form-state';
import { FormState } from '../../utils/state/types';
import { createControlledComponent } from '../../utils/ControlledComponent/ControlledComponent';
import {
  createFormContext,
  FormContext,
} from '../../utils/context/contextFactory';
import { createFormActions, FormControllerActions } from './Actions/actions';
import { createWixSdkAdapter } from '../../utils/sdkAdapterFactory';
import { FormApi } from '../../api/FormApi';
import { mapConfigToState } from '../../utils/dummies/editor-behaviour';
import { createFormBiLogger } from '../../utils/bi/biLoggerFactory';
import { initialTpaPlugins } from '../../tpa-plugins';
import { ExperimentsConsts } from '../../consts/experiments';
import { extractAppSection } from '../../api/PageAPIAdapter/URLAdapter/utils';

const createController: CreateControllerFn = async ({
  flowAPI: flowApi,
}: ControllerParams) => {
  let rerender: (
    propsToUpdate: Partial<FormState | { actions: FormControllerActions }>,
  ) => void = () => {};
  return {
    async pageReady() {
      const {
        controllerConfig,
        translations: { t },
        reportError,
        httpClient,
        experiments,
      } = flowApi;

      extractAppSection(flowApi);

      const bookingsDataCapsule = await BookingsDataCapsule.getInstance(
        flowApi as any,
      );
      const wixSdkAdapter = createWixSdkAdapter(controllerConfig, experiments);
      const formApi = new FormApi({
        httpClient,
        bookingsDataCapsule,
        wixSdkAdapter,
        reportError,
        experiments,
      });

      const shouldInitializeBiLogger = flowApi?.bi! && !wixSdkAdapter.isSSR();
      const biLogger = shouldInitializeBiLogger
        ? createFormBiLogger({
            viewerBiLogger: flowApi?.bi!,
            wixSdkAdapter,
          })
        : undefined;

      const user = controllerConfig.wixCodeApi.user.currentUser;
      const formContext: FormContext = createFormContext({
        t,
        flowApi,
        wixSdkAdapter,
        bookingsDataCapsule,
        formApi,
        biLogger,
        reportError,
        user,
      });

      const { render, actions, onStateChange } =
        await createControlledComponent<
          any,
          FormControllerActions,
          FormContext
        >({
          controllerConfig,
          initialState: {
            status: FormStatus.INITIALIZING,
          } as Partial<FormState>,
          actionsFactory: createFormActions,
          context: formContext,
          flowApi,
        });
      rerender = render;

      if (
        experiments.enabled(ExperimentsConsts.AdditionalTimeSlotsInFormPlugin)
      ) {
        await initialTpaPlugins({ actions, controllerConfig });
      }

      if (!wixSdkAdapter.isSSR()) {
        onStateChange((state) => {
          biLogger?.update(state);
        });
      }

      wixSdkAdapter.onUserLogin(actions.onLogin);
    },
    updateConfig($w, config) {
      const stateUpdate = mapConfigToState(config);
      rerender(stateUpdate);
    },
  };
};

export default createController;
